import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';
import { InfoCircleOutlined } from '@ant-design/icons';
import { getCurrentBrowserFingerPrint } from '@rajesh896/broprint.js';

import { useScript } from '../../../CustomHooks/useScript';
import Loading from '../../loadingScreen/Loading';
import ErrorModal from '../../modals/errorModal/ErrorModal';
import { getStandaloneGooglePayConfig, GOOGLE_PAY_SCRIPT_URL, STANDALONE_SUBMIT_INSTRUMENT_TYPE } from './googlePayConfig';
import { submitStandaloneGooglePayPayment } from '../../../../slices/StandaloneGooglePaySlice';
import { setPaymentInitiationData } from '../../../../slices/PaymentInitiationSlice';

let fingerPrintValue = '';
getCurrentBrowserFingerPrint().then((fingerprint) => {
  fingerPrintValue = fingerprint;
});

function StandaloneGooglePayButton({ onPaymentResult }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isScriptLoaded = useScript(GOOGLE_PAY_SCRIPT_URL);
  const sessionDetails = useSelector((state) => state.paymentSessionDetails.sessionDetails);
  const totalPaymentSummaryValue = useSelector((state) => state.additionalComponents.totalPaymentSummaryValue);
  const surchargeDetail = useSelector((state) => state.surcharges.surchargeDetail);
  const containerRef = useRef(null);
  const paymentsClientRef = useRef(null);
  const initAttempted = useRef(false);
  const totalPriceRef = useRef(null);
  const displayItemsRef = useRef(null);
  const walletSurchargeIdsRef = useRef([]);
  const [isButtonReady, setIsButtonReady] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [debugMessage, setDebugMessage] = useState('init pending');
  const config = useMemo(() => getStandaloneGooglePayConfig(sessionDetails), [sessionDetails]);

  const walletSurcharges = useMemo(() => (
    surchargeDetail?.appliedSurcharges?.filter((surcharge) => {
      const details = surcharge?.surchargeDetails;
      if (details?.applicableOn && details.applicableOn !== 'Wallet') return false;
      const network = details?.network?.toLowerCase();
      return !network || network === 'googlepay';
    }) ?? []
  ), [surchargeDetail]);

  const walletSurchargeIds = useMemo(
    () => walletSurcharges.map((s) => s.surchargeDetails?.surchargeCode),
    [walletSurcharges],
  );

  const baseAmount = Number(sessionDetails?.paymentDetails?.money?.amount);
  const surchargeTotal = walletSurcharges.reduce((sum, s) => sum + Number(s.calculatedSurchargeFee || 0), 0);
  const computedTotal = walletSurcharges.length > 0 ? baseAmount + surchargeTotal : null;
  const totalPrice = Number(totalPaymentSummaryValue || computedTotal || baseAmount || 0).toFixed(2);
  const currencyCode = sessionDetails?.paymentDetails?.money?.currencyCode;
  const countryCode = sessionDetails?.paymentDetails?.context?.countryCode;

  const displayItems = walletSurcharges.length > 0 ? [
    {
      label: 'Subtotal',
      type: 'SUBTOTAL',
      status: 'FINAL',
      price: baseAmount.toFixed(2),
    },
    ...walletSurcharges.map((s) => ({
      label: s.surchargeDetails?.title ?? 'Surcharge',
      type: 'LINE_ITEM',
      status: 'FINAL',
      price: Number(s.calculatedSurchargeFee).toFixed(2),
    })),
  ] : undefined;

  useEffect(() => { totalPriceRef.current = totalPrice; }, [totalPrice]);
  useEffect(() => { displayItemsRef.current = displayItems; }, [displayItems]);
  useEffect(() => { walletSurchargeIdsRef.current = walletSurchargeIds; }, [walletSurchargeIds]);

  const sendEncryptedTokenToBackend = useCallback(async (encryptedToken) => {
    const token = sessionDetails?.token;
    const payload = {
      instrumentDetails: {
        type: STANDALONE_SUBMIT_INSTRUMENT_TYPE,
        wallet: { token: encryptedToken },
      },
      browserData: {
        browserLanguage: navigator.language,
        userAgentHeader: navigator.userAgent,
        timeZoneOffSet: new Date().getTimezoneOffset(),
        browserFingerprint: fingerPrintValue,
      },
    };

    if (walletSurchargeIdsRef.current.length) {
      payload.surcharges = walletSurchargeIdsRef.current;
    }

    return dispatch(submitStandaloneGooglePayPayment({ token, payload })).unwrap();
  }, [sessionDetails, dispatch]);

  const getPaymentDataRequest = useCallback(() => {
    // additionalData.allowedPaymentMethods is used as-is (deep-cloned so we don't
    // mutate redux state) — only tokenizationSpecification is added on top.
    const allowedPaymentMethods = JSON.parse(JSON.stringify(config.allowedPaymentMethods)).map((method) => ({
      ...method,
      tokenizationSpecification: {
        type: 'PAYMENT_GATEWAY',
        parameters: {
          gateway: config.gateway,
          gatewayMerchantId: config.gatewayMerchantId,
        },
      },
    }));

    return {
      apiVersion: 2,
      apiVersionMinor: 0,
      allowedPaymentMethods,
      merchantInfo: {
        ...(config.merchantId && { merchantId: config.merchantId }),
        merchantName: config.merchantName || 'Boxpay',
      },
      transactionInfo: {
        totalPriceStatus: 'FINAL',
        totalPrice: totalPriceRef.current,
        totalPriceLabel: 'Total',
        currencyCode,
        countryCode,
        ...(displayItemsRef.current && { displayItems: displayItemsRef.current }),
      },
    };
  }, [config, currencyCode, countryCode]);

  const handleButtonClick = useCallback(async () => {
    const client = paymentsClientRef.current;
    if (!client) return;

    setIsProcessing(true);
    try {
      const paymentDataRequest = getPaymentDataRequest();
      const paymentData = await client.loadPaymentData(paymentDataRequest);
      const encryptedToken = paymentData?.paymentMethodData?.tokenizationData?.token;

      const result = await sendEncryptedTokenToBackend(encryptedToken);

      // PaymentResponseHandler handles every action type (redirect, html, ...).
      console.log('[StandaloneGooglePay] backend response, dispatching to PaymentResponseHandler', result);
      dispatch(setPaymentInitiationData(result));
      onPaymentResult?.({ success: true, data: result });
    } catch (err) {
      // Google Pay throws statusCode 'CANCELED' when the shopper dismisses the sheet.
      if (err?.statusCode !== 'CANCELED') {
        setShowErrorModal(true);
        onPaymentResult?.({ success: false, error: err });
      }
    } finally {
      setIsProcessing(false);
    }
  }, [getPaymentDataRequest, sendEncryptedTokenToBackend, onPaymentResult]);

  useEffect(() => {
    if (initAttempted.current) return;

    if (!isScriptLoaded) {
      setDebugMessage('waiting for pay.js script to load');
      return;
    }
    if (!window.google?.payments?.api) {
      setDebugMessage('pay.js loaded but window.google.payments.api missing');
      return;
    }
    if (!config.gateway || !config.gatewayMerchantId || !config.allowedPaymentMethods.length) {
      setDebugMessage(`missing config — gateway: ${config.gateway || 'MISSING'}, gatewayMerchantId: ${config.gatewayMerchantId || 'MISSING'}, allowedPaymentMethods: ${config.allowedPaymentMethods.length ? 'OK' : 'MISSING'}`);
      return;
    }

    initAttempted.current = true;
    setDebugMessage('calling isReadyToPay...');

    const client = new window.google.payments.api.PaymentsClient({
      environment: config.environment,
    });
    paymentsClientRef.current = client;

    const isReadyToPayRequest = {
      apiVersion: 2,
      apiVersionMinor: 0,
      allowedPaymentMethods: config.allowedPaymentMethods,
    };

    client.isReadyToPay(isReadyToPayRequest).then((response) => {
      if (!response?.result) {
        setDebugMessage('isReadyToPay returned result: false — Google says no eligible payment method in this browser/profile');
        return;
      }
      if (!containerRef.current) {
        setDebugMessage('isReadyToPay returned true but containerRef is null');
        return;
      }

      const button = client.createButton({
        onClick: handleButtonClick,
        buttonColor: 'white',
        buttonType: 'plain',
        buttonSizeMode: 'fill',
      });
      containerRef.current.appendChild(button);
      setIsButtonReady(true);
      setDebugMessage('button ready');
    }).catch((err) => {
      setDebugMessage(`isReadyToPay threw: ${err?.statusCode || ''} ${err?.statusMessage || err?.message || JSON.stringify(err)}`);
      setIsButtonReady(false);
    });
  }, [isScriptLoaded, config, handleButtonClick]);

  return (
    <>
      {isProcessing && (
        <Loading
          text={t('PLEASE_WAIT_PROCESSING_PAYMENT')}
          subText={t('NOTE_AVOID_BACK_BUTTON')}
        />
      )}

      <ErrorModal
        title={t('PAYMENT_FAILED')}
        desc={t('PAYMENT_RETRY_NOTICE')}
        showModal={showErrorModal}
        primaryOnClick={() => setShowErrorModal(false)}
        primaryBtnText={t("RETRY")}
        icon={<InfoCircleOutlined style={{ color: '#FF4D4F' }} />}
      />

      <div className="payment-container">
        {!isButtonReady && (
          <Skeleton.Button active style={{ width: '100%', height: 40 }} shape="default" />
        )}
        <div ref={containerRef} style={{ display: isButtonReady ? 'block' : 'none' }} />
      </div>
    </>
  );
}

export default StandaloneGooglePayButton;
